const ALLOWED_TAGS = new Set([
  'p', 'br', 'b', 'i', 'em', 'strong', 'u', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'a',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'blockquote',
  'pre', 'code', 'sub', 'sup', 'small',
]);

const ALLOWED_ATTRS = new Set([
  'class', 'style', 'href', 'target', 'rel', 'colspan', 'rowspan',
]);

const isSafeHref = (href) => {
  const v = (href ?? '').trim().toLowerCase();
  if (!v) return false;
  // Block scriptable protocols.
  if (
    v.startsWith('javascript:') ||
    v.startsWith('data:') ||
    v.startsWith('vbscript:')
  ) {
    return false;
  }
  if (v.startsWith('http://') || v.startsWith('https://')) return true;
  if (v.startsWith('mailto:')) return true;
  // Allow relative links and same-page anchors.
  if (v.startsWith('/') || v.startsWith('#')) return true;
  return false;
};

const isSafeStyle = (style) => {
  const v = (style ?? '').toLowerCase();
  if (!v) return false;
  // Keep this intentionally conservative. Inline CSS can be abused.
  const blockedSubstrings = ['expression', 'javascript:', 'url(', '@import'];
  return !blockedSubstrings.some((s) => v.includes(s));
};

export default function sanitizeHtml(dirty) {
  if (dirty == null || dirty === '') return '';
  const doc = new DOMParser().parseFromString(String(dirty), 'text/html');

  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (!ALLOWED_TAGS.has(child.tagName.toLowerCase())) {
          // If the element itself is disallowed, still sanitize its descendants.
          walk(child);
          child.replaceWith(...child.childNodes);
          return;
        }
        [...child.attributes].forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          if (!ALLOWED_ATTRS.has(attrName)) {
            child.removeAttribute(attr.name);
            return;
          }

          if (attrName === 'href' && !isSafeHref(attr.value)) {
            child.removeAttribute(attr.name);
            return;
          }

          if (attrName === 'style' && !isSafeStyle(attr.value)) {
            child.removeAttribute(attr.name);
          }
        });
        if (child.tagName.toLowerCase() === 'a') {
          child.setAttribute('rel', 'noopener noreferrer');
        }
        walk(child);
      }
    });
  };

  walk(doc.body);
  return doc.body.innerHTML;
}
